import React from 'react';
import { UserCheck, AlertCircle } from 'lucide-react';
import type { TeamSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './TeamCollaborationGroup.module.css';

interface Props {
  data: TeamSection;
}

export function TeamCollaborationGroup({ data }: Props) {
  const totalManagers = data.permissionDistribution.full + data.permissionDistribution.restricted;
  const maxUploads = data.topPhotographers[0]?.count ?? 1;

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><UserCheck size={15} /></div>
        <div>
          <div className={shared['group-title']}>Team &amp; Collaboration</div>
          <div className={shared['group-subtitle']}>Is your crew set up and contributing?</div>
        </div>
      </div>

      <div className={shared['stat-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Active managers</span>
          <span className={shared['stat-value']}>{data.activeManagers}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Pending invites</span>
          <span className={`${shared['stat-value']} ${data.pendingInvitations > 0 ? shared['stat-value--amber'] : ''}`}>
            {data.pendingInvitations}
          </span>
          {data.pendingInvitations > 0 && <span className={shared['stat-sub']}>haven't accepted yet</span>}
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>FTP active (month)</span>
          <span className={shared['stat-value']}>{data.activeFtpThisMonth}</span>
        </div>
      </div>

      {data.pendingInvitations > 0 && (
        <div className={styles['pending-notice']}>
          <AlertCircle size={13} className={styles['pending-icon']} />
          <span className={styles['pending-text']}>
            {data.pendingInvitations} team member{data.pendingInvitations > 1 ? 's have' : ' has'} not yet accepted their invitation.
          </span>
        </div>
      )}

      {/* Permission distribution */}
      {totalManagers > 0 && (
        <div>
          <div className={shared['section-label']}>Permission distribution ({totalManagers} managers)</div>
          <div className={styles['perm-bar-wrap']}>
            <div
              className={styles['perm-bar-full']}
              style={{ width: totalManagers > 0 ? `${(data.permissionDistribution.full / totalManagers) * 100}%` : '0%' }}
              title={`Full access: ${data.permissionDistribution.full}`}
            />
            <div
              className={styles['perm-bar-restricted']}
              style={{ width: totalManagers > 0 ? `${(data.permissionDistribution.restricted / totalManagers) * 100}%` : '0%' }}
              title={`Restricted: ${data.permissionDistribution.restricted}`}
            />
          </div>
          <div className={styles['perm-legend']}>
            <span className={styles['perm-leg']}><span className={styles['dot-full']} />Full access: {data.permissionDistribution.full}</span>
            <span className={styles['perm-leg']}><span className={styles['dot-restricted']} />Restricted: {data.permissionDistribution.restricted}</span>
          </div>
        </div>
      )}

      {/* Top photographers */}
      {data.topPhotographers.length > 0 && (
        <div>
          <div className={shared['section-label']}>Top photographers this account</div>
          <div className={shared['ranked-list']}>
            {data.topPhotographers.map((p, i) => (
              <div key={p.ftpAccountId} className={shared['ranked-item']}>
                <div className={shared['rank-badge']}>{i + 1}</div>
                <div className={styles['photographer-info']}>
                  <span className={shared['ranked-label']}>{p.photographerName || 'Unnamed'}</span>
                  {p.eventName && <span className={styles['photographer-event']}>{p.eventName}</span>}
                </div>
                <div className={shared['ranked-bar']} style={{ width: `${Math.round((p.count / maxUploads) * 80)}px` }} />
                <span className={shared['ranked-count']}>{p.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
